/**
 * Registry of Grafana dashboards exposed as sub-menu items under the top-level
 * "Grafana" nav entry.
 *
 * Persistence moved from browser localStorage to the data-api
 * (`GET/PUT /v2/grafana/dashboards`, table `grafana_dashboards`) so the list
 * survives dev-server port changes, browser resets, and different machines --
 * the localStorage version silently vanished on any of those. Entries a
 * previous build left under the old `settings.grafanaDashboards` key are
 * migrated into the DB once, on the first load that finds an empty server list.
 */
import { useCallback, useEffect, useRef } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";

import { api } from "../api/client";

export type GrafanaDashboard = {
  id: string;
  title: string;
  url: string;
};

export const GRAFANA_DASHBOARDS_KEY = "settings.grafanaDashboards";

export function generateDashboardId(): string {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  return `dash_${Math.random().toString(36).slice(2, 10)}${Date.now().toString(36)}`;
}

/**
 * Ensure the URL includes Grafana's kiosk mode. Grafana accepts `?kiosk`
 * (or `&kiosk`) with no value; we add it only if not already present.
 */
export function withKiosk(url: string): string {
  const trimmed = url.trim();
  if (!trimmed) return trimmed;
  // Match any existing kiosk / kiosk=... parameter (case-insensitive).
  if (/[?&]kiosk(?:=[^&#]*)?(?:$|[&#])/i.test(trimmed)) return trimmed;
  const hashIdx = trimmed.indexOf("#");
  const base = hashIdx >= 0 ? trimmed.slice(0, hashIdx) : trimmed;
  const hash = hashIdx >= 0 ? trimmed.slice(hashIdx + 1) : "";
  const sep = base.includes("?") ? "&" : "?";
  const withParam = `${base}${sep}kiosk`;
  return hash ? `${withParam}#${hash}` : withParam;
}

function sanitizeDashboard(raw: unknown): GrafanaDashboard | null {
  if (!raw || typeof raw !== "object") return null;
  const obj = raw as Record<string, unknown>;
  const url = typeof obj.url === "string" ? obj.url.trim() : "";
  if (!url) return null;
  const title = typeof obj.title === "string" && obj.title.trim() ? obj.title.trim() : "Dashboard";
  const id = typeof obj.id === "string" && obj.id.trim() ? obj.id.trim() : generateDashboardId();
  return { id, title, url };
}

/**
 * Read ONLY the entries a previous build persisted under
 * `settings.grafanaDashboards`. Unlike the removed `readDashboards`, this does
 * NOT fall back to the legacy single-URL key or the `VITE_GRAFANA_URL` env
 * seed -- we only migrate *real* operator-added entries into the server-side
 * registry so an env default never silently repopulates the DB.
 */
function readStoredDashboards(): GrafanaDashboard[] {
  try {
    const raw = localStorage.getItem(GRAFANA_DASHBOARDS_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed
      .map(sanitizeDashboard)
      .filter((d): d is GrafanaDashboard => d !== null);
  } catch {
    return [];
  }
}

const QUERY_KEY = ["grafana-dashboards"] as const;
// Per-origin flag so the localStorage -> DB migration runs at most once.
const MIGRATED_FLAG_KEY = "settings.grafanaDashboards.migratedToDb";

/**
 * React hook returning the current dashboard list plus a setter, backed by the
 * data-api instead of localStorage. The setter keeps the value-or-updater
 * signature the Settings page used before; writes update the query cache
 * optimistically and are debounced into a single replace-all PUT so
 * per-keystroke edits don't spray requests.
 */
export function useGrafanaDashboards(): [
  GrafanaDashboard[],
  (next: GrafanaDashboard[] | ((prev: GrafanaDashboard[]) => GrafanaDashboard[])) => void,
] {
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: QUERY_KEY,
    queryFn: ({ signal }) => api.grafanaDashboards.list({ signal }),
    // The cache is the source of truth while editing (optimistic writes +
    // in-progress blank rows). Auto-refetch would clobber a row the operator
    // is still typing, so hold the data steady and refresh only on demand.
    staleTime: Infinity,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });
  const items = q.data ?? [];

  // One-time migration of any real localStorage entries into an empty DB list.
  const migratedRef = useRef(false);
  useEffect(() => {
    if (migratedRef.current || !q.isSuccess) return;
    migratedRef.current = true;
    try {
      if (localStorage.getItem(MIGRATED_FLAG_KEY) === "1") return;
      if ((q.data?.length ?? 0) === 0) {
        const local = readStoredDashboards();
        if (local.length > 0) {
          void api.grafanaDashboards
            .save(local)
            .then((saved) => qc.setQueryData(QUERY_KEY, saved))
            .catch(() => { /* keep localStorage; retry on next load */ });
        }
      }
      localStorage.setItem(MIGRATED_FLAG_KEY, "1");
    } catch {
      /* localStorage unavailable -- nothing to migrate */
    }
  }, [q.isSuccess, q.data, qc]);

  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pendingRef = useRef<GrafanaDashboard[] | null>(null);

  const flush = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    const toSave = pendingRef.current;
    pendingRef.current = null;
    if (!toSave) return;
    // Only persist complete rows: a freshly added row starts with an empty
    // url (rejected by the request schema) and shouldn't hit the server until
    // the operator has filled it in. The optimistic cache keeps the blank row
    // visible for editing; we deliberately do NOT overwrite the cache with the
    // server response so an in-progress row is never yanked out mid-type.
    const clean = toSave.filter((d) => d.url.trim() !== "");
    void api.grafanaDashboards
      .save(clean)
      .catch(() => { void qc.invalidateQueries({ queryKey: QUERY_KEY }); });
  }, [qc]);

  // Flush any pending debounced write when the editing component unmounts.
  useEffect(() => () => flush(), [flush]);

  const setDashboards = useCallback(
    (next: GrafanaDashboard[] | ((prev: GrafanaDashboard[]) => GrafanaDashboard[])) => {
      const prev = qc.getQueryData<GrafanaDashboard[]>(QUERY_KEY) ?? [];
      const value = typeof next === "function"
        ? (next as (p: GrafanaDashboard[]) => GrafanaDashboard[])(prev)
        : next;
      qc.setQueryData(QUERY_KEY, value); // optimistic
      pendingRef.current = value;
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(flush, 500);
    },
    [qc, flush],
  );

  return [items, setDashboards];
}
